import { getAllClients } from "../services/ClientsService";

export interface ClientMapResult {
  clientMap: Record<string, string>;
  clientCounts: Record<string, number>;
  clientPhones: Record<string, string>;
}

const pickFirstNonEmptyString = (...values: any[]): string | undefined => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    } else if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

export const buildClientMap = async (
  branches: any[],
  opts?: { debug?: boolean }
): Promise<ClientMapResult> => {
  const debug = opts?.debug ?? false;
  const branchIds = branches
    .map((branch: any) => branch.business_id || branch.id || branch._id)
    .filter(Boolean);
  if (debug) console.debug("buildClientMap: branchIds", branchIds);

  const clientPromises = branchIds.map((id: string) =>
    getAllClients(id).catch((err) => {
      if (debug) console.warn(`getAllClients failed for ${id}:`, err);
      return [];
    })
  );

  const clientResponses = await Promise.all(clientPromises);
  const clientMap: Record<string, string> = {};
  const clientCounts: Record<string, number> = {};
  const clientPhones: Record<string, string> = {};

  clientResponses.forEach((rawResp, idx) => {
    if (debug) console.debug("buildClientMap: rawResp[", idx, "] =", rawResp);
    const branchId = branchIds[idx];
    const branchKey = branchId ? String(branchId) : undefined;

    let clientsArray: any[] = [];
    if (Array.isArray(rawResp)) {
      clientsArray = rawResp;
    } else if (rawResp && typeof rawResp === "object") {
      clientsArray =
        rawResp.users ||
        rawResp.clients ||
        rawResp.data ||
        rawResp.items ||
        [];

      if (
        !Array.isArray(clientsArray) &&
        rawResp.data &&
        Array.isArray(rawResp.data.users)
      ) {
        clientsArray = rawResp.data.users;
      }

      if (!Array.isArray(clientsArray)) {
        const candidate = Object.values(rawResp).find((v) => Array.isArray(v));
        if (Array.isArray(candidate)) clientsArray = candidate as any[];
      }
    }

    if (!Array.isArray(clientsArray)) {
      if (branchKey) clientCounts[branchKey] = 0;
      return;
    }

    if (branchKey) {
      clientCounts[branchKey] = clientsArray.length;
    }

    clientsArray.forEach((client: any) => {
      if (!client || typeof client !== "object") return;

      const clientId =
        client.id ||
        client._id ||
        client.user_id ||
        client.client_id ||
        client.customer_id;
      const clientName =
        client.fullName ||
        client.name ||
        client.displayName ||
        `${client.firstName || ""} ${client.lastName || ""}`.trim();

      const finalId = clientId || (clientName ? clientName : undefined);
      const finalName = clientName || String(finalId || "");

      if (finalId) {
        const key = String(finalId);
        clientMap[key] = finalName;

        const resolvedPhone = pickFirstNonEmptyString(
          client.phoneNumber,
          client.phone,
          client.mobile,
          client.phone_number,
          client.phoneNo,
          client.phone_no,
          client.primaryPhone,
          client.primary_phone,
          client.contact_number,
          client.contactNumber,
          client.whatsapp,
          client.whatsapp_number,
          client.customer_phone,
          client.client_phone
        );
        if (resolvedPhone) {
          clientPhones[key] = resolvedPhone;
        }
      }
    });
  });

  if (debug)
    console.debug(
      "buildClientMap: result map size=",
      Object.keys(clientMap).length
    );
  return { clientMap, clientCounts, clientPhones };
};

export const buildAllAppointments = (
  branches: any[],
  clientMap: Record<string, string>,
  clientPhones?: Record<string, string>
): any[] => {
  const allAppointments: any[] = [];

  const normalizeTime = (t: any) => {
    if (!t) return t;
    try {
      const s = String(t).trim();
      const hasAmPm = /\b(AM|PM)\b/i.test(s);
      if (!hasAmPm && /:/g.test(s)) {
        const [hh, mm] = s.split(":");
        const h = Number(hh);
        if (Number.isNaN(h) || Number.isNaN(Number(mm))) return s;
        return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
      }
      const match = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let hh = Number(match[1]);
        const mm = match[2];
        const ampm = match[3].toUpperCase();
        if (ampm === "PM" && hh !== 12) hh += 12;
        if (ampm === "AM" && hh === 12) hh = 0;
        return `${String(hh).padStart(2, "0")}:${mm}`;
      }
    } catch (err) {
      return String(t);
    }
    return String(t);
  };

  branches.forEach((branch: any) => {
    if (!branch.appointments || !Array.isArray(branch.appointments)) return;

    branch.appointments.forEach((appointment: any) => {
      const clientKeyCandidates = [
        appointment.client_id,
        appointment.clientId,
        appointment.client,
        appointment.client_name,
        appointment.clientName,
      ].filter(Boolean);

      let resolvedClientName: string | undefined;
      for (const key of clientKeyCandidates) {
        if (key === undefined || key === null) continue;
        const k = String(key);
        if (clientMap[k]) {
          resolvedClientName = clientMap[k];
          break;
        }
      }

      const clientMissing =
        !resolvedClientName &&
        !appointment.client_name &&
        !appointment.clientName;

      const clientDisplay =
        clientMissing
          ? "Client deleted"
          : resolvedClientName ||
            appointment.client_name ||
            appointment.clientName ||
            clientKeyCandidates[0] ||
            "";

      let clientIdRaw: any = undefined;
      if (
        appointment.client_id !== undefined &&
        appointment.client_id !== null
      )
        clientIdRaw = appointment.client_id;
      else if (
        appointment.clientId !== undefined &&
        appointment.clientId !== null
      )
        clientIdRaw = appointment.clientId;
      else if (appointment.client && typeof appointment.client === "object") {
        clientIdRaw =
          appointment.client.id ||
          appointment.client._id ||
          appointment.client.user_id ||
          appointment.client.client_id;
      } else if (appointment.client) clientIdRaw = appointment.client;
      else if (appointment.client_name) clientIdRaw = appointment.client_name;
      else if (appointment.clientName) clientIdRaw = appointment.clientName;

      const clientId =
        clientIdRaw === undefined || clientIdRaw === null
          ? undefined
          : String(clientIdRaw);

      const createdAt =
        appointment.created_at ||
        appointment.createdAt ||
        appointment.created ||
        appointment.created_date ||
        appointment.createdDate ||
        appointment.createdOn ||
        appointment.created_on ||
        appointment.dateCreated ||
        appointment.date_created ||
        appointment.createdTime ||
        appointment.created_time ||
        appointment.createdTimestamp ||
        appointment.created_timestamp ||
        appointment.timestamp ||
        appointment.date;

      const normalizedStart = normalizeTime(
        appointment.time_start ||
          appointment.timeStart ||
          appointment.startTime
      );
      const normalizedEnd = normalizeTime(
        appointment.time_end || appointment.timeEnd || appointment.endTime
      );

      const resolvedPhone = clientMissing
        ? ""
        : pickFirstNonEmptyString(
            clientId ? clientPhones?.[clientId] : undefined,
            appointment.client_phone,
            appointment.clientPhone,
            appointment.phone_number,
            appointment.phoneNumber,
            appointment.phone,
            appointment.mobile,
            branch.business_phone
          ) || "";

      allAppointments.push({
        id: appointment.id,
        clientName: clientDisplay,
        client_id: clientId,
        clientId: clientId,
        bookingSource: appointment.notes,
        phoneNumber: resolvedPhone,
        priority: appointment.flag,
        date: appointment.start_date || appointment.startDate || appointment.date,
        start_date: appointment.start_date || appointment.startDate || appointment.date,
        branch: branch.name,
        branchId: branch.business_id || branch.id || branch._id,
        status: appointment.status,
        startTime: normalizedStart,
        endTime: normalizedEnd,
        time_start: normalizedStart,
        time_end: normalizedEnd,
        servicePrice:
          appointment.servicePrice ||
          appointment.serviceprice ||
          appointment.serviceprice,
        serviceprice:
          appointment.servicePrice ||
          appointment.serviceprice ||
          appointment.serviceprice,
        service_id:
          appointment.service_id ||
          appointment.serviceId ||
          appointment.service ||
          appointment.svc_id ||
          null,
        promoCodeUsed: appointment.promo_code_used,
        notes: appointment.notes,
        createdAt,
        created_at: createdAt,
      });
    });
  });

  return allAppointments;
};
